<script setup lang="ts">
const props = defineProps<{
  minigame: TMiniGameTemplate;
}>();

const { playMiniGame } = useSmarticoMiniGames();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();

const buyInCost = computed(() => {
  if (props.minigame.buyin_cost_points) {
    const value = props.minigame.buyin_cost_points;

    return {
      value,
      icon: 'currency-header/coin',
      name: value === 1 ? 'COIN' : 'COINS',
      color: '#FF9B50',
    };
  }
  if (props.minigame.buyin_cost_diamonds) {
    const value = props.minigame.buyin_cost_diamonds;

    return {
      value,
      icon: 'currency-header/diamond',
      name: value === 1 ? 'DIAMOND' : 'DIAMONDS',
      color: '#00C9FF',
    };
  }
  if (props.minigame.buyin_cost_gems) {
    const value = props.minigame.buyin_cost_gems;

    return {
      value,
      icon: 'currency-header/gem',
      name: value === 1 ? 'GEM' : 'GEMS',
      color: '#81FFA1',
    };
  }

  return null;
});

const buttonText = computed(() => {
  switch (props.minigame.saw_game_type) {
    case 'giftbox':
      return 'ABRIR';

    case 'scratch':
      return 'RASPAR';

    case 'matchx':
      return 'PALPITAR';

    case 'quiz':
      return 'RESPONDER';

    case 'wheel':
      return 'ABRIR';

    case 'coin_flip':
      return 'JOGAR';

    default:
      return 'ABRIR';
  }
});

const buttonIcon = computed(() => {
  switch (props.minigame.saw_game_type) {
    case 'giftbox':
      return 'mini-games/abrir';

    case 'scratch':
      return 'mini-games/raspadinha-coin';

    case 'matchx':
      return 'messages';

    case 'quiz':
      return 'pen_square';

    case 'wheel':
      return 'mini-games/play';

    case 'coin_flip':
      return 'mini-games/girar';

    default:
      return 'mini-games/play';
  }
});

async function handlePlayMiniGame() {
  if (!isAuthenticated.value) {
    openLogin();

    return;
  }
  await playMiniGame(props.minigame.id);
}
</script>

<template>
  <div
    v-if="minigame.saw_game_type !== 'matchx'"
    class="minigame-card"
  >
    <!-- Header: Imagem + Info -->
    <div class="minigame-card__header">
      <SmarticoApostatudoSmarticoImage
        :alt="minigame.name"
        class="minigame-card__image"
        :src="minigame.promo_image"
      />
      <div class="minigame-card__info">
        <div class="minigame-card__title">
          {{ minigame.name }}
        </div>
        <div class="minigame-card__description">
          {{ minigame.promo_text }}
        </div>
      </div>
    </div>

    <!-- Preço -->
    <div class="minigame-card__price">
      <NuxtIcon
        v-if="buyInCost"
        class="minigame-card__price-icon"
        :filled="true"
        :name="buyInCost.icon"
      />
      <span
        v-if="buyInCost"
        class="minigame-card__price-value"
        :style="{ color: buyInCost.color }"
      >
        {{ buyInCost.value.toLocaleString('pt-BR') }} {{ buyInCost.name }}
      </span>
      <span
        v-else
        class="minigame-card__price-value minigame-card__price-value--free"
      >
        GRÁTIS
      </span>
    </div>

    <!-- Botão -->
    <button
      class="minigame-card__button"
      @click="handlePlayMiniGame"
    >
      <NuxtIcon
        class="minigame-card__button-icon"
        :name="buttonIcon"
      />
      <span>{{ buttonText }}</span>
    </button>
  </div>
</template>

<style scoped>
/* Card */
.minigame-card {
  width: 253px;
  height: auto;
  background: var(--vipcard-bg);
  border: 1px solid var(--vipcard-border);
  border-radius: 10px;
  padding: 10px 5px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  user-select: none;
  box-sizing: border-box;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

/* Header: Imagem + Info */
.minigame-card__header {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  height: 87px;
}

.minigame-card__image {
  width: 87px;
  height: 87px;
  border-radius: 8px;
  flex-shrink: 0;
  object-fit: contain;
}

.minigame-card__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  height: 87px;
}

.minigame-card__title {
  font-size: 12px;
  font-weight: 700;
  color: white;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  line-height: 1.17;
}

.minigame-card__description {
  font-size: 10px;
  font-weight: 400;
  color: var(--vipcard-text-muted);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
  line-height: 1.3;
}

/* Preço */
.minigame-card__price {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 0;
  background: var(--vipcard-surface);
  border-radius: 5px;
  flex-shrink: 0;
}

.minigame-card__price-icon {
  font-size: 15px;
  flex-shrink: 0;
}

.minigame-card__price-value {
  font-size: 10px;
  font-weight: 800;
  white-space: nowrap;
}

.minigame-card__price-value--free {
  color: var(--vipcard-accent);
}

/* Botão */
.minigame-card__button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 30px;
  padding: 0 7px;
  background: var(--vipcard-btn-bg);
  border: none;
  border-radius: 4.5px;
  color: white;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  cursor: pointer;
  transition: filter 0.2s;
  flex-shrink: 0;
}

.minigame-card__button:hover {
  filter: brightness(1.1);
}

.minigame-card__button-icon {
  font-size: 13px;
}
</style>
